import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { apiGet, apiPost, resetSessionClient } from "@/shared/api/client";
import { queryKeys } from "@/shared/api/query-keys";
import type { MeDto, SessionResult, SwitchMembershipBody } from "@/shared/api/types";
import { announceOrgChange } from "./orgChannel";
import type { PermissionSet } from "./permissions";

/** A signed-in user inside an organisation. Routes under the shell only render with one of these. */
export interface Session {
  user: MeDto["user"];
  tenant: NonNullable<MeDto["tenant"]>;
  role: NonNullable<MeDto["role"]>;
  permissions: PermissionSet;
  memberships: MeDto["memberships"];
  pendingInvites: MeDto["pendingInvites"];
}

const SessionContext = createContext<Session | null>(null);
/** The signed-in account with or without an organisation (onboarding pages, the switcher, invitations). */
const MeContext = createContext<MeDto | null>(null);

export function SessionProvider({ value, children }: { value: Session; children: ReactNode }) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function MeProvider({ value, children }: { value: MeDto; children: ReactNode }) {
  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useOptionalSession(): Session | null {
  return useContext(SessionContext);
}

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession must be used inside an authenticated route");
  return s;
}

export function useMe(): MeDto {
  const me = useContext(MeContext);
  if (!me) throw new Error("useMe must be used inside a signed-in route");
  return me;
}

export function useTenantId(): string {
  return useSession().tenant.id;
}

export function toSession(me: MeDto): Session | null {
  if (!me.tenant || !me.role) return null;
  return {
    user: me.user,
    tenant: me.tenant,
    role: me.role,
    permissions: me.role.permissions,
    memberships: me.memberships,
    pendingInvites: me.pendingInvites,
  };
}

/** The account read: the gate's, the sign-in page's, and the move handler's while it waits for the new organisation. */
export function meQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.session.me(),
    queryFn: ({ signal }) => apiGet<MeDto>("/me", undefined, signal),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useMeQuery() {
  return useQuery(meQueryOptions());
}

/**
 * A new session (verify, switch, accept, create) means a new cache: nothing from the previous user or
 * organisation survives (rules/01 §3, belt and braces), and the next expired session may redirect again.
 * `tenantId` is where the session is now: every other tab of this browser is on it too (one cookie), so
 * they are told and follow (ADR-0011 addendum 2026-10-04, plan §5.6).
 */
export function beginSession(queryClient: QueryClient, tenantId?: string | null): void {
  resetSessionClient();
  queryClient.clear();
  if (tenantId) announceOrgChange(tenantId);
}

/** Logout clears the cookie server-side and drops every cached row client-side (belt and braces). */
export function useLogout(onDone: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<Record<string, never>>("/auth/logout"),
    onSettled: () => {
      resetSessionClient();
      queryClient.clear();
      onDone();
    },
  });
}

/** Switch the session to another membership of the same user; the caller navigates to the overview. */
export function useSwitchOrganisation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SwitchMembershipBody) => apiPost<SessionResult>("/auth/switch", body),
    onSuccess: (result) => beginSession(queryClient, result.tenant?.id ?? null),
  });
}
